import { useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { PageHeader, Loading, StatusBadge } from "@/components/common";
import { MaintDialog } from "@/components/dialogs";
import { api, useFetch, errMsg, fmtDate, money } from "@/lib/api";
import { useCurrency } from "@/context/AuthContext";

const COLS = [["reported", "Reported"], ["in_progress", "In progress"], ["scheduled", "Scheduled"], ["resolved", "Resolved"]];

function Detail({ item, onClose, onSaved }) {
  const cur = useCurrency();
  const [note, setNote] = useState("");
  const [contractor, setContractor] = useState(item?.contractor || "");
  const [cost, setCost] = useState(item?.cost ?? "");
  const [shared, setShared] = useState(!!item?.shared_with_landlord);
  if (!item) return null;
  const save = async () => {
    try {
      await api.patch(`/biz/maintenance/${item.id}`, { contractor, cost: cost === "" ? null : Number(cost), note: note || null, shared_with_landlord: shared });
      toast.success("Updated");
      setNote("");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Sheet open={!!item} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="maint-detail-sheet">
        <SheetHeader><SheetTitle className="font-display text-2xl text-left">{item.title}</SheetTitle></SheetHeader>
        <div className="mt-2 text-sm text-slate-500">{item.property_name} · reported {fmtDate(item.created_at)} by {item.reported_by} ({item.reporter_role})</div>
        <div className="flex gap-2 mt-3"><StatusBadge status={item.priority} /><StatusBadge status={item.status} /></div>
        {item.description && <p className="mt-4 text-sm whitespace-pre-wrap">{item.description}</p>}
        <div className="flex items-center gap-3 mt-6 rounded-lg border border-slate-200 p-3">
          <Switch checked={shared} onCheckedChange={setShared} data-testid="maint-share-landlord" />
          <div><Label>Share with landlord</Label><p className="text-xs text-slate-500">The property's landlord sees this issue and its notes in their portal and can reply (emailed when first shared). The renter never sees landlord replies.</p></div>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-6">
          <div className="space-y-1.5"><Label>Contractor</Label><Input value={contractor} onChange={(e) => setContractor(e.target.value)} data-testid="maint-contractor" /></div>
          <div className="space-y-1.5"><Label>Cost</Label><Input type="number" step="0.01" value={cost} onChange={(e) => setCost(e.target.value)} data-testid="maint-cost" /></div>
        </div>
        <div className="space-y-1.5 mt-3"><Label>Add a note</Label><Textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="maint-note" /></div>
        <Button onClick={save} className="mt-3 bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="maint-save">Save</Button>
        {item.cost != null && <p className="text-xs text-slate-500 mt-2">Recorded cost: {money(item.cost, cur)}</p>}
        <div className="mt-6 space-y-3">
          {(item.notes || []).map((n, i) => (
            <div key={i} className={`rounded-lg border p-3 text-sm ${n.role === "landlord" ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"}`}>{n.role === "landlord" && <span className="inline-block text-[10px] font-semibold uppercase tracking-wider text-emerald-800 mb-1" data-testid="note-landlord-badge">Landlord reply</span>}<div>{n.text}</div><div className="text-xs text-slate-500 mt-1">{n.by} · {fmtDate(n.at)}</div></div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default function Maintenance() {
  const { data, reload, setData } = useFetch("/biz/maintenance");
  const { data: props } = useFetch("/biz/properties");
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(null);
  const move = async (m, status) => {
    setData((d) => d.map((x) => (x.id === m.id ? { ...x, status } : x)));
    try { await api.patch(`/biz/maintenance/${m.id}`, { status }); toast.success("Status updated"); } catch (e) { toast.error(errMsg(e)); reload(); }
  };
  return (
    <div data-testid="maintenance-page">
      <PageHeader eyebrow="Repairs" title="Maintenance" subtitle="Requests from you and your renters, from reported to resolved."
        actions={<Button onClick={() => setOpen(true)} disabled={!props?.length} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="add-maint-button"><Plus className="w-4 h-4 mr-1" />Log request</Button>} />
      {!data ? <Loading /> : (
        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
          {COLS.map(([key, label]) => {
            const items = data.filter((m) => m.status === key);
            return (
              <div key={key} className="bg-slate-100/70 border border-slate-200 rounded-xl p-3 min-h-[200px]" data-testid={`maint-col-${key}`}>
                <div className="flex justify-between items-center px-1 mb-3"><span className="text-sm font-bold">{label}</span><span className="text-xs text-slate-500">{items.length}</span></div>
                <div className="space-y-3">
                  {items.map((m) => (
                    <div key={m.id} className="bg-white border border-slate-200 rounded-lg p-4 lift" data-testid={`maint-card-${m.id}`}>
                      <button className="text-left w-full" onClick={() => setSel(m)} data-testid={`maint-open-${m.id}`}>
                        <div className="flex justify-between gap-2"><span className="font-semibold text-sm">{m.title}</span><StatusBadge status={m.priority} /></div>
                        <div className="text-xs text-slate-500 mt-1">{m.property_name}</div>
                        <div className="text-xs text-slate-400 mt-1">{fmtDate(m.created_at)} · {m.reporter_role === "renter" ? "from renter" : m.reported_by}</div>
                      </button>
                      <Select value={m.status} onValueChange={(v) => move(m, v)}>
                        <SelectTrigger className="h-8 mt-3 text-xs" data-testid={`maint-status-${m.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{COLS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <MaintDialog open={open} onOpenChange={setOpen} properties={props} onSaved={reload} />
      <Detail key={sel?.id} item={sel} onClose={() => setSel(null)} onSaved={() => { reload(); setSel(null); }} />
    </div>
  );
}
